/**
 * KAN-845: экран "Начало работы" приложения менеджера (/m/start).
 * Открывается сам после входа по ссылке и с вкладки "Внимание", пока не
 * сделаны обязательные шаги и менеджер не нажал "Готово"; повторно из профиля.
 * Без состояния перехода (иконка на главном экране сохранила этот адрес)
 * после "Готово" или когда всё сделано, ведёт на /m.
 * KAN-846: первый шаг "Придумайте логин и пароль" у входа по ссылке; пока он
 * не сделан, "Готово" недоступно.
 * KAN-873: пометка строки настройки открывает экран к своему шагу
 * (onboardingStepNavState): карточка шага подсвечена и прокручена.
 * Логика шагов в useOnboardingSteps, здесь только отрисовка.
 */
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { InstallInstructions } from '@/components/mobile/InstallHint';
import { PushControls, TelegramControls } from '@/components/mobile/MobileNotificationsBlock';
import { OnboardingCredentialsForm } from '@/components/mobile/onboarding/OnboardingCredentialsForm';
import { OnboardingPhoneForm } from '@/components/mobile/onboarding/OnboardingPhoneForm';
import { OnboardingStepCard } from '@/components/mobile/onboarding/OnboardingStepCard';
import { useOnboardingSteps } from '@/hooks/useOnboardingSteps';
import { MOBILE_HOME } from '@/lib/mobileApp';
import {
  getOnboardingNavStep,
  isOnboardingNavState,
  ONBOARDING_TEXTS,
  type OnboardingStep,
} from '@/lib/managerOnboarding';

type Onboarding = ReturnType<typeof useOnboardingSteps>;

function StepBody({ step, onboarding }: { step: OnboardingStep; onboarding: Onboarding }): JSX.Element | null {
  // Сделанные логин, установка и телефон сворачиваются; уведомления и Telegram оставляют кнопку "Выключить".
  if (step.done && (step.id === 'credentials' || step.id === 'install' || step.id === 'phone')) return null;
  switch (step.id) {
    case 'credentials':
      return <OnboardingCredentialsForm />;
    case 'install':
      return (
        <>
          <p className="text-sm text-gray-600">{ONBOARDING_TEXTS.installNote}</p>
          <InstallInstructions kind={onboarding.install.kind} install={onboarding.install.install} />
        </>
      );
    case 'phone':
      return <OnboardingPhoneForm />;
    case 'push':
      return <PushControls push={onboarding.push} iosHint={ONBOARDING_TEXTS.pushIos} />;
    case 'telegram':
      return onboarding.notificationsStatus ? <TelegramControls status={onboarding.notificationsStatus} /> : null;
    default:
      return null;
  }
}

const TITLES: Record<OnboardingStep['id'], string> = {
  credentials: ONBOARDING_TEXTS.credentials,
  install: ONBOARDING_TEXTS.install,
  phone: ONBOARDING_TEXTS.phone,
  push: ONBOARDING_TEXTS.push,
  telegram: ONBOARDING_TEXTS.telegram,
};

export function MobileStartPage(): JSX.Element {
  const onboarding = useOnboardingSteps();
  const location = useLocation();
  const navigate = useNavigate();

  if (!onboarding.hasManager) return <Navigate to={MOBILE_HOME} replace />;
  const openedOnPurpose = isOnboardingNavState(location.state);
  const focusStep = getOnboardingNavStep(location.state);
  if (!openedOnPurpose && onboarding.ready && (onboarding.dismissed || onboarding.allDone)) {
    return <Navigate to={MOBILE_HOME} replace />;
  }

  const finish = () => {
    onboarding.dismiss();
    navigate(MOBILE_HOME, { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-gray-50 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]"
      data-testid="mobile-start-page"
    >
      <main className="mx-auto max-w-xl px-4 py-5">
        <h1 className="text-2xl font-semibold text-gray-900">{ONBOARDING_TEXTS.title}</h1>
        <p className="mt-1 text-base text-gray-600">{ONBOARDING_TEXTS.intro}</p>
        {!onboarding.ready ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
          </div>
        ) : (
          <ol className="mt-4 space-y-3">
            {onboarding.steps.map((step, i) => (
              <OnboardingStepCard
                key={step.id}
                id={step.id}
                index={i + 1}
                title={step.id === 'install' && step.done ? ONBOARDING_TEXTS.installDone : TITLES[step.id]}
                done={step.done}
                optional={step.optional}
                highlighted={step.id === focusStep}
              >
                <StepBody step={step} onboarding={onboarding} />
              </OnboardingStepCard>
            ))}
          </ol>
        )}
        {onboarding.needsCredentials && (
          <p className="mt-6 text-center text-sm text-gray-600" data-testid="onboarding-finish-blocked">
            {ONBOARDING_TEXTS.finishNeedsCredentials}
          </p>
        )}
        <button
          type="button"
          onClick={finish}
          disabled={onboarding.needsCredentials}
          className="mt-6 flex min-h-[56px] w-full items-center justify-center rounded-xl bg-blue-600 px-4 text-base font-semibold text-white active:bg-blue-700 disabled:opacity-50"
        >
          {ONBOARDING_TEXTS.finish}
        </button>
      </main>
    </div>
  );
}
